// ─── AttachmentList — uploads grid (DataTable style) ─────────────────────────

import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import type { AttachmentMetaResponse } from '../types/attachment';

// ─── Skeleton rows ────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <tr aria-hidden="true">
      {[60, 40, 50, 45, 50, 90].map((w, i) => (
        <td key={i} style={{ padding: 'var(--spacing-3) var(--spacing-3)' }}>
          <div
            style={{
              height: 14,
              width: `${w}%`,
              background:
                'linear-gradient(90deg, var(--color-surface-elevated), color-mix(in srgb, var(--color-surface-elevated) 50%, var(--color-surface-muted)), var(--color-surface-elevated))',
              borderRadius: 'var(--radius-sm)',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

// ─── File icon ────────────────────────────────────────────────────────────────

function FileIcon({ mimeType }: { mimeType: string }) {
  let label = '?';
  let colorVar = 'var(--color-text-secondary)';
  let bgMix = 'color-mix(in srgb, var(--color-text-secondary) 12%, transparent)';
  let borderColor = 'var(--color-text-muted)';

  if (mimeType.startsWith('image/png')) {
    label = 'PNG';
    colorVar = 'var(--color-success)';
    bgMix = 'color-mix(in srgb, var(--color-success) 18%, transparent)';
    borderColor = 'var(--color-success)';
  } else if (mimeType.startsWith('image/jpeg')) {
    label = 'JPG';
    colorVar = 'var(--color-info)';
    bgMix = 'color-mix(in srgb, var(--color-info) 18%, transparent)';
    borderColor = 'var(--color-info)';
  } else if (mimeType.startsWith('image/gif')) {
    label = 'GIF';
    colorVar = 'var(--color-info)';
    bgMix = 'color-mix(in srgb, var(--color-info) 18%, transparent)';
    borderColor = 'var(--color-info)';
  } else if (mimeType.startsWith('image/webp')) {
    label = 'WEBP';
    colorVar = 'var(--color-info)';
    bgMix = 'color-mix(in srgb, var(--color-info) 18%, transparent)';
    borderColor = 'var(--color-info)';
  } else if (mimeType === 'application/pdf') {
    label = 'PDF';
    colorVar = 'var(--color-danger)';
    bgMix = 'color-mix(in srgb, var(--color-danger) 18%, transparent)';
    borderColor = 'var(--color-danger)';
  } else if (mimeType === 'text/csv') {
    label = 'CSV';
    colorVar = 'var(--color-warning)';
    bgMix = 'color-mix(in srgb, var(--color-warning) 18%, transparent)';
    borderColor = 'var(--color-warning)';
  } else if (
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    label = 'XLS';
    colorVar = 'var(--color-success)';
    bgMix = 'color-mix(in srgb, var(--color-success) 24%, transparent)';
    borderColor = 'var(--color-success)';
  } else if (mimeType === 'text/plain') {
    label = 'TXT';
  }

  return (
    <span
      aria-hidden="true"
      style={{
        width: 28,
        height: 28,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--font-size-xs)',
        fontFamily: 'var(--font-family-mono)',
        fontWeight: 700,
        flexShrink: 0,
        background: bgMix,
        color: colorVar,
        border: `1px solid ${borderColor}`,
      }}
    >
      {label}
    </span>
  );
}

// ─── TTL Pill — restored (#620: preview + expiry now live) ───────────────────

/** "PT22H58M" 류 ISO-8601 Duration 문자열 → 총 분(minutes) */
function parseIsoDurationMinutes(iso: string): number {
  const match = /^PT(?:(\d+)H)?(?:(\d+)M)?(?:([\d.]+)S)?$/.exec(iso);
  if (!match) return 0;
  const hours = Number(match[1] ?? 0);
  const minutes = Number(match[2] ?? 0);
  const seconds = Number(match[3] ?? 0);
  return hours * 60 + minutes + seconds / 60;
}

function TtlPill({ timeToExpire }: { timeToExpire: string }) {
  const { t } = useTranslation();
  const totalMinutes = parseIsoDurationMinutes(timeToExpire);

  if (totalMinutes <= 0) {
    return <StatusPill tone="danger">{t('attachmentDemo.ttl.expired')}</StatusPill>;
  }
  if (totalMinutes < 60) {
    return (
      <StatusPill tone="warning">
        {t('attachmentDemo.ttl.minutesLeft', { n: Math.ceil(totalMinutes) })}
      </StatusPill>
    );
  }
  return (
    <StatusPill tone="neutral">
      {t('attachmentDemo.ttl.hoursLeft', { n: Math.floor(totalMinutes / 60) })}
    </StatusPill>
  );
}

// ─── Format helpers ───────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AttachmentListProps {
  items: AttachmentMetaResponse[];
  isLoading: boolean;
  onDownload: (fileId: string, originalName: string) => void;
  onPreview: (fileId: string) => void;
  onDeleteRequest: (fileId: string, originalName: string) => void;
  selectedFileId?: string | null;
}

// ─── AttachmentList ───────────────────────────────────────────────────────────

export function AttachmentList({
  items,
  isLoading,
  onDownload,
  onPreview,
  onDeleteRequest,
  selectedFileId,
}: AttachmentListProps) {
  const { t } = useTranslation();

  const COL_HEADERS = [
    { key: 'filename', label: t('attachmentDemo.list.col.filename') },
    { key: 'size', label: t('attachmentDemo.list.col.size') },
    { key: 'type', label: t('attachmentDemo.list.col.type') },
    { key: 'uploadedAt', label: t('attachmentDemo.list.col.uploadedAt') },
    { key: 'expiresAt', label: t('attachmentDemo.list.col.expiresAt') },
    { key: 'actions', label: t('attachmentDemo.list.col.actions') },
  ];

  return (
    <div
      style={{
        background: 'var(--color-surface-background)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          borderBottom: '1px solid var(--color-surface-border)',
          flexWrap: 'wrap',
          gap: 'var(--spacing-2)',
        }}
      >
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
          }}
        >
          {t('attachmentDemo.list.title')}
          {!isLoading && (
            <span
              style={{
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 700,
                padding: '1px var(--spacing-2)',
                borderRadius: 'var(--radius-full)',
              }}
            >
              {items.length}
            </span>
          )}
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          {t('attachmentDemo.list.meta')}
        </span>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table
          aria-label={t('attachmentDemo.list.ariaLabel')}
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <thead>
            <tr>
              {COL_HEADERS.map((h) => (
                <th
                  key={h.key}
                  scope="col"
                  style={{
                    textAlign: 'left',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: 'var(--color-surface-elevated)',
                    color: 'var(--color-text-secondary)',
                    fontWeight: 700,
                    fontSize: 'var(--font-size-xs)',
                    borderBottom: '1px solid var(--color-surface-border)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} />)
              : items.map((item) => (
                  <tr
                    key={item.fileId}
                    aria-selected={selectedFileId === item.fileId}
                    style={{
                      background:
                        selectedFileId === item.fileId
                          ? 'color-mix(in srgb, var(--color-brand-primary) 12%, transparent)'
                          : undefined,
                    }}
                  >
                    {/* Filename */}
                    <td
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        verticalAlign: 'middle',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 'var(--spacing-2)',
                          fontWeight: 600,
                          maxWidth: 220,
                        }}
                      >
                        <FileIcon mimeType={item.mimeType} />
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={item.originalName}
                        >
                          {item.originalName}
                        </span>
                      </span>
                    </td>

                    {/* Size */}
                    <td
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        verticalAlign: 'middle',
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatBytes(item.size)}
                    </td>

                    {/* MIME */}
                    <td
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        verticalAlign: 'middle',
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.mimeType}
                    </td>

                    {/* Uploaded at */}
                    <td
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        verticalAlign: 'middle',
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatDateTime(item.createdAt)}
                    </td>

                    {/* TTL — restored #620 */}
                    <td
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        verticalAlign: 'middle',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <TtlPill timeToExpire={item.timeToExpire} />
                    </td>

                    {/* Actions */}
                    <td
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        borderBottom: '1px solid var(--color-surface-border)',
                        verticalAlign: 'middle',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          gap: 'var(--spacing-1)',
                          alignItems: 'center',
                        }}
                      >
                        {/* Download */}
                        <button
                          type="button"
                          aria-label={`${item.originalName} ${t('attachmentDemo.list.action.download')}`}
                          title={t('attachmentDemo.list.action.download')}
                          onClick={() => onDownload(item.fileId, item.originalName)}
                          style={{
                            width: 28,
                            height: 28,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid var(--color-surface-border)',
                            background: 'var(--color-surface-elevated)',
                            color: 'var(--color-text-secondary)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-xs)',
                            fontFamily: 'inherit',
                          }}
                        >
                          ⬇
                        </button>

                        {/* Preview — restored #620 */}
                        <button
                          type="button"
                          aria-label={`${item.originalName} ${t('attachmentDemo.list.action.preview')}`}
                          title={t('attachmentDemo.list.action.preview')}
                          onClick={() => onPreview(item.fileId)}
                          aria-pressed={selectedFileId === item.fileId}
                          style={{
                            width: 28,
                            height: 28,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${
                              selectedFileId === item.fileId
                                ? 'var(--color-brand-primary)'
                                : 'var(--color-surface-border)'
                            }`,
                            background:
                              selectedFileId === item.fileId
                                ? 'color-mix(in srgb, var(--color-brand-primary) 16%, transparent)'
                                : 'var(--color-surface-elevated)',
                            color:
                              selectedFileId === item.fileId
                                ? 'var(--color-brand-primary)'
                                : 'var(--color-text-secondary)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-xs)',
                            fontFamily: 'inherit',
                          }}
                        >
                          👁
                        </button>

                        {/* Delete */}
                        <button
                          type="button"
                          aria-label={`${item.originalName} ${t('attachmentDemo.list.action.delete')}`}
                          title={t('attachmentDemo.list.action.delete')}
                          onClick={() =>
                            onDeleteRequest(item.fileId, item.originalName)
                          }
                          style={{
                            width: 28,
                            height: 28,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid var(--color-surface-border)',
                            background: 'var(--color-surface-elevated)',
                            color: 'var(--color-text-secondary)',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-size-xs)',
                            fontFamily: 'inherit',
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.color =
                              'var(--color-danger)';
                            (e.currentTarget as HTMLButtonElement).style.borderColor =
                              'var(--color-danger)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLButtonElement).style.color =
                              'var(--color-text-secondary)';
                            (e.currentTarget as HTMLButtonElement).style.borderColor =
                              'var(--color-surface-border)';
                          }}
                        >
                          🗑
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
